import { DatasetAccessLevel } from "datasets/types/enums/access-level";

export interface SensitiveDataConfig {
  mask_data_trusted_users_enabled: boolean;
  mask_data_participant_count_threshold: number;
}

const PREVIEW_USER_MASKING_DEFAULT: SensitiveDataConfig = {
  mask_data_trusted_users_enabled: true,
  mask_data_participant_count_threshold: 5,
};

/**
 * Whether the user has only preview (restrictive) access to the dataset.
 * Used to enforce consistent masking of small counts on list and detail views.
 */
export function isPreviewUserByAccessLevel(
  accessLevel: DatasetAccessLevel | string | null | undefined
): boolean {
  return (
    accessLevel === null || accessLevel === undefined || accessLevel === DatasetAccessLevel.PREVIEW
  );
}

/**
 * Effective sensitive-data config for display: preview users always get
 * masking (threshold 5) when server config has it disabled, so list and
 * detail views stay consistent and small counts are not leaked.
 */
export function getEffectiveSensitiveDataConfig(
  accessLevel: DatasetAccessLevel | string | null | undefined,
  sensitiveDataConfig: SensitiveDataConfig | null
): { isPreviewUser: boolean; effectiveSensitiveDataConfig: SensitiveDataConfig | null } {
  const isPreviewUser = isPreviewUserByAccessLevel(accessLevel);
  if (!isPreviewUser) {
    return { isPreviewUser: false, effectiveSensitiveDataConfig: sensitiveDataConfig };
  }
  if (!sensitiveDataConfig || !sensitiveDataConfig.mask_data_trusted_users_enabled) {
    return {
      isPreviewUser: true,
      effectiveSensitiveDataConfig: PREVIEW_USER_MASKING_DEFAULT,
    };
  }
  return {
    isPreviewUser: true,
    effectiveSensitiveDataConfig: sensitiveDataConfig,
  };
}

/**
 * Format a participant count, optionally masking when below a threshold.
 *
 * @param count - The count to format (e.g. total cohort size, criterion count).
 * @param formatter - Optional Intl.NumberFormat for numeric display.
 * @param sensitiveDataConfig - Config for when to mask (threshold and enabled flag).
 */
export function formatParticipantCount(
  count: number | null | undefined,
  formatter?: Intl.NumberFormat,
  sensitiveDataConfig?: SensitiveDataConfig | null
): string {
  if (count == null) {
    return "";
  }
  if (count === 0) {
    return "0";
  }
  if (isCountLessThanThreshold(count, sensitiveDataConfig)) {
    return `<${sensitiveDataConfig?.mask_data_participant_count_threshold}`;
  }
  return formatter ? formatter.format(count) : String(count);
}

export function isCountLessThanThreshold(
  count: number | null | undefined,
  sensitiveDataConfig?: SensitiveDataConfig | null
): boolean {
  if (count == null) {
    return false;
  }
  if (sensitiveDataConfig == null) {
    return false;
  }
  if (
    sensitiveDataConfig?.mask_data_trusted_users_enabled &&
    sensitiveDataConfig.mask_data_participant_count_threshold &&
    count < sensitiveDataConfig.mask_data_participant_count_threshold
  ) {
    return true;
  }
  return false;
}
